/**
 * @file Download Button Component.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Button from './Button';
import { PhotoDownloadURL } from '../../graphql/queries/photos';
import { IMAGE_SIZE_CODE } from '../../constants/image-size-codes';

class DownloadPhotoButton extends Component {
  static propTypes = {
    photoId: PropTypes.string.isRequired,
    photoSize: PropTypes.oneOf(Object.values(IMAGE_SIZE_CODE)).isRequired,
    includeWatermark: PropTypes.bool,
    client: PropTypes.shape({
      query: PropTypes.func.isRequired,
    }).isRequired,
    children: PropTypes.node,
    onDownload: PropTypes.func,
    className: PropTypes.string,
  };

  state = {
    loading: false,
    downloadUrl: null,
    error: null,
  };

  componentDidUpdate(prevProps) {
    const { includeWatermark, photoId } = this.props;
    const {
      includeWatermark: prevIncludeWatermark,
      photoId: prevPhotoId,
    } = prevProps;

    if (photoId !== prevPhotoId) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        loading: false,
        downloadUrl: null,
      });
    }
    // If the user changes whether they want the watermark added, set
    // the downloadUrl to null (to ensure a fresh image is generated)
    // Note: It is initially null anyway
    if (includeWatermark !== prevIncludeWatermark) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        downloadUrl: null,
      });
    }
  }

  fetchDownloadLink = async () => {
    const { onDownload } = this.props;

    this.setState(() => ({ loading: true }));

    try {
      const { client, photoId, photoSize, includeWatermark } = this.props;

      const {
        data: {
          photoDownloadUrls: { photo_urls: photoUrls },
        },
      } = await client.query({
        query: PhotoDownloadURL,
        variables: {
          photos: [
            {
              photo_id: photoId,
              photo_size: photoSize.toUpperCase(),
              include_watermark: includeWatermark,
            },
          ],
        },
        fetchPolicy: 'network-only',
      });
      const [downloadUrl] = photoUrls;

      this.setState(
        () => ({
          downloadUrl,
          loading: false,
        }),
        () => {
          if (downloadUrl) {
            this.button.click(); // Trigger download right away.
            if (onDownload) {
              onDownload();
            }
          }
        },
      );
    } catch (err) {
      this.setState(() => ({ error: err }));
    } finally {
      this.setState(() => ({ loading: false }));
    }
  };

  render() {
    const {
      client,
      photoId,
      photoSize,
      includeWatermark,
      children,
      onDownload,
      className,
      ...props
    } = this.props;

    const { loading, downloadUrl, error } = this.state;
    const child = error ? 'Error. Please try again later' : children;
    const classes = classNames({
      'is-loading': loading,
      'has-error': !!error,
      [className]: !!className,
    });

    return (
      <Button
        disabled={loading}
        onClick={!downloadUrl ? () => this.fetchDownloadLink() : undefined}
        href={downloadUrl || undefined}
        inputRef={btn => {
          this.button = btn;
        }}
        {...props}
        className={classes}
      >
        {child}
      </Button>
    );
  }
}

export default DownloadPhotoButton;
